import Image from 'next/image'
import { FC, HTMLAttributes, useEffect, useState } from 'react'
import type { Product } from '@commerce/types/product'
import usePrice from '@framework/product/use-price'
import { Container, Text, useUI } from '@components/ui'
import Storyblok, { StoryblokContent } from '@lib/storyblok'
import { ProductContent } from 'types/storyblok'
import CallToAction from './CallToAction'
import { ProductOptions, OptionDescriptions } from '@components/product'
import { useAddItem } from '@framework/cart'
import { useRouter } from 'next/router'
import s from './DiagnosticView.module.css'

import {
  getProductVariant,
  selectDefaultOptionFromProduct,
  SelectedOptions,
} from '../helpers'

import productImage from '../../../public/lifeforce/products.jpg'
import { CartItemBody } from '@commerce/types/cart'
import { Typography } from '@components/design'
import { DynamicComponent, RichTextRenderer } from '@components/common'
import { trackAddToCart, trackViewItem } from '@lib/datalayer'
import ProductSchema from '@lib/product-schema'

const DiagnosticParagraph: FC<HTMLAttributes<HTMLParagraphElement>> = ({
  children,
}) => {
  return (
    <Typography variant="body-lg" color="white" className="capitalize">
      {children}
    </Typography>
  )
}

const DiagnosticList: FC<any> = ({ children, ...rest }) => {
  return (
    <ul
      {...rest}
      style={{
        listStyle: 'none',
        marginLeft: '0',
      }}
      className="py-4 flex flex-nowrap xl:flex-wrap flex-col xl:flex-row"
    >
      {children}
    </ul>
  )
}
const DiagnosticListItem: FC<any> = ({ children }) => {
  return (
    <li className={s.listBottomLeftTriangle}>
      <Typography variant="body">{children}</Typography>
    </li>
  )
}
interface ProductViewProps {
  product: Product
  story: ProductContent
}

const DiagnosticView: FC<ProductViewProps> = ({ product, story }) => {

  // ignore additional DX variants
  // product.options = [product.options[0]]

  // add to cart
  const addItem = useAddItem()
  const { openSidebar } = useUI()
  const [loading, setLoading] = useState(false)
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({})
  const htmlDescription = story
    ? Storyblok.richTextResolver.render(story.description)
    : ''

  const router = useRouter()

  useEffect(() => {
    selectDefaultOptionFromProduct(product, setSelectedOptions)
    ProductSchema(product)
  }, [product])

  useEffect(() => {
    if (router.pathname === `/product/[slug]`) {
      trackViewItem(product)
    }
  }, [router])

  const getOptionSelectionsForBC = () =>
    Object.entries(selectedOptions).map(
      ([productDisplayName, optionChoiceLabel]) => {
        const option = product.options.find(
          (option) => option.displayName.toLowerCase() === productDisplayName
        )
        if (!option) {
          return console.error(
            'Could not find option',
            productDisplayName,
            option
          )
        }
        const choice = option.values.find(
          (value) => value.label.toLowerCase() === optionChoiceLabel
        )
        if (!choice) {
          return console.error(
            'Could not find choice',
            option,
            optionChoiceLabel
          )
        }
        return {
          option_id: option.id,
          option_value: choice.entityId,
        }
      }
    )

  const variant = getProductVariant(product, selectedOptions)
  const addToCart = async () => {
    setLoading(true)
    try {
      const optionSelections = getOptionSelectionsForBC()

      const cartItemBody = {
        productId: String(product.id),
        variantId: String(variant ? variant.id : product.variants[0].id),
      } as CartItemBody
      if (!!optionSelections.length) {
        // @ts-ignore
        cartItemBody['optionSelections'] = optionSelections || []
      }
      await addItem(cartItemBody)
      trackAddToCart(product, 1)
      openSidebar()
      setLoading(false)
    } catch (err) {
      setLoading(false)
    }
  }

  const { price } = usePrice({
    amount: variant?.prices.price.value!,
    baseAmount: Number(variant?.prices.retailPrice),
    currencyCode: variant?.prices.price.currencyCode!,
  })

  const optionsDescriptions: OptionDescriptions[] = [
    {
      displayName: 'location',
      descriptions: [
        {
          label: 'in-home appointment',
          description:
            'A phlebotomist reaches out to schedule an appointment in the comfort of your home - subject to availability.',
        },
        {
          label: 'walk-in',
          description: 'Visit your nearest Labcorp for a walk-in blood draw.',
        },
      ],
    },
  ]

  return (
    <div>
      <div className="bg-gray-900 relative">
        <div
          className="xl:absolute bottom-0 xl:w-3/6 h-64 xl:h-4/5 bg-cover xl:bg-contain bg-no-repeat bg-left-bottom"
          style={{ backgroundImage: `url(${story?.main_image?.filename})` }}
        ></div>
        <Container className="max-w-6xl mx-auto w-full pb-16 hello" clean>
          <div className="grid grid-cols-1 xl:grid-cols-12 relative inset-y-4">
            <div className="absolute border-t-1 w-1/3 h-1/3" />
            <div className="m-7 col-span-7 col-start-6 py-4 text-white">
              <Typography
                variant="h5"
                component="h5"
                className="font-bold uppercase"
                color="gray"
              >
                {story?.need_description}
              </Typography>
              <Typography
                variant="h2"
                component="h2"
                className="font-bold uppercase py-2"
                color="white"
              >
                {story && story?.name?.length ? story.name : product.name}
              </Typography>
              <RichTextRenderer
                richTextStoryblok={story.description}
                mask={{
                  p: DiagnosticParagraph,
                  ul: DiagnosticList,
                  li: DiagnosticListItem,
                }}
              />
              <div className="mt-4">
                <ProductOptions
                  options={product.options}
                  selectedOptions={selectedOptions}
                  setSelectedOptions={setSelectedOptions}
                  theme="dark"
                  displayInline={true}
                  optionsDescriptions={optionsDescriptions}
                />
              </div>
              <CallToAction
                story={story}
                addToCart={addToCart}
                loading={loading}
                price={price}
                frequency={selectedOptions.frequency || 'one-time'}
                availableForSale={variant?.availableForSale || true}
              />
            </div>
          </div>
        </Container>
      </div>
      <div className="w-full px-0">
        {story.content_bloks &&
          story.content_bloks.map((blok: StoryblokContent) => (
            <DynamicComponent blok={blok} key={blok._uid} />
          ))}
      </div>
    </div>
  )
}

export default DiagnosticView
